import React, { useState, useEffect } from 'react'
import { MagnifyingGlass, Check, X } from '@phosphor-icons/react'
import { materialsApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import { inputClass, inputStyle, labelClass, labelStyle } from './_kit'

interface Material {
  id: number
  title: string
  material_type: string
  content_type: string
  processing_status: string
}

interface MaterialSelectorProps {
  selectedIds: number[]
  onChange: (ids: number[]) => void
  // Optional allowlist of content_type values; when provided, materials of
  // any other type are hidden. Used by the deck node so websites etc. don't
  // appear there. Omit to show every type (e.g. for AI email context).
  allowedContentTypes?: Set<string>
}

const contentTypeIcons: Record<string, string> = {
  pdf: 'PDF',
  docx: 'DOC',
  pptx: 'PPT',
  website: 'WEB',
  text: 'TXT',
  excel: 'XLS',
  csv: 'CSV',
  markdown: 'MD',
}

const MaterialSelector: React.FC<MaterialSelectorProps> = ({ selectedIds, onChange, allowedContentTypes }) => {
  const [materials, setMaterials] = useState<Material[]>([])
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setIsLoading(true)
    materialsApi.getAll()
      .then((res) => setMaterials(res.data || []))
      .catch(() => setMaterials([]))
      .finally(() => setIsLoading(false))
  }, [])

  const filtered = materials.filter(m =>
    m.title.toLowerCase().includes(search.toLowerCase()) &&
    m.processing_status === 'completed' &&
    (!allowedContentTypes || allowedContentTypes.has(m.content_type))
  )

  const toggle = (id: number) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter(i => i !== id))
    } else {
      onChange([...selectedIds, id])
    }
  }

  // Names the material list for AT. Generated, not hardcoded — this selector is
  // mounted by more than one config panel and can appear twice on a screen.
  const materialsLabelId = React.useId()

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span id={materialsLabelId} className={labelClass} style={labelStyle}>
          Material Sources
        </span>
        <span className="text-muted-foreground dark:text-[#8a8f98]" style={labelStyle}>{selectedIds.length} selected</span>
      </div>
      <div className="relative">
        <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search materials..."
          aria-label="Search materials"
          className={cn(inputClass, 'pl-8')}
          style={inputStyle}
        />
      </div>
      <div
        className="max-h-[360px] overflow-y-auto border border-border/60 dark:border-white/[0.08] rounded-md divide-y divide-border/30 dark:divide-white/[0.04]"
        role="group"
        aria-labelledby={materialsLabelId}
      >
        {isLoading ? (
          <div className="p-3 text-xs text-muted-foreground text-center">Loading materials...</div>
        ) : filtered.length === 0 ? (
          <div className="p-3 text-xs text-muted-foreground text-center">
            {materials.length === 0 ? 'No materials uploaded yet' : 'No matching materials'}
          </div>
        ) : (
          filtered.map((material) => {
            const isSelected = selectedIds.includes(material.id)
            return (
              <button
                key={material.id}
                type="button"
                onClick={() => toggle(material.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors duration-150 ${
                  isSelected ? 'bg-[#5e6ad2]/[0.08] dark:bg-[#5e6ad2]/[0.12]' : 'hover:bg-muted/60 dark:hover:bg-white/[0.03]'
                }`}
              >
                <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors duration-150 ${
                  isSelected ? 'bg-[#5e6ad2] text-white' : 'border border-border/60 dark:border-white/[0.14]'
                }`}>
                  {isSelected && <Check className="w-3 h-3" weight="bold" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-foreground dark:text-[#f7f8f8] truncate">{material.title}</p>
                  <p className="text-[10px] text-muted-foreground dark:text-[#8a8f98]">
                    {contentTypeIcons[material.content_type] || material.content_type}
                  </p>
                </div>
              </button>
            )
          })
        )}
      </div>
      {selectedIds.length > 0 && (
        <button
          type="button"
          onClick={() => onChange([])}
          className="text-xs text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] flex items-center gap-1 transition-colors duration-150"
        >
          <X className="w-3 h-3" /> Clear all
        </button>
      )}
    </div>
  )
}

export default MaterialSelector
